import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  InputBase,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Snackbar,
  Typography,
  Alert,
  Switch,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import CloseIcon from '@mui/icons-material/Close';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';

import type { PermissionLevel, ObjectType } from '@/shared/types/permission';
import {
  useGetPermissionsForObjectQuery,
  useUpsertPermissionMutation,
  useDeletePermissionMutation,
} from '@/features/permissions/permissionsApi';
import { useGetMyTeamsQuery } from '@/features/teams/teamsApi';
import { useAppSelector } from '@/shared/hooks/redux';
import { isFeatureEnabled } from '@/shared/libs/featureFlags';
import UserAvatar from '@/shared/components/UserAvatar';

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  objectId: string;
  objectType: ObjectType;
  ownerId: string;
  /** Optional title shown in the dialog header, e.g. "My Lesson Plan" */
  title?: string;
  /** Current public visibility state of the object */
  isPublic?: boolean;
  /** Callback to toggle public visibility — omit to hide the toggle (e.g. rubrics) */
  onTogglePublic?: (isPublic: boolean) => Promise<any> | void;
  /** Canonical URL for the shared object (fixes list-page copy-link bug) */
  shareUrl?: string;
}

const ROLE_LABELS: Record<PermissionLevel, string> = { viewer: 'Viewer', editor: 'Editor' };

export const ShareDialog: React.FC<ShareDialogProps> = ({
  open,
  onClose,
  objectId,
  objectType,
  ownerId,
  title,
  isPublic,
  onTogglePublic,
  shareUrl,
}) => {
  const teamManagementEnabled = isFeatureEnabled('teamManagement');
  const { data: perms = [] } = useGetPermissionsForObjectQuery(
    { object_type: objectType, object_id: objectId },
    { skip: !open }
  );
  const { data: teams = [] } = useGetMyTeamsQuery(undefined, {
    skip: !open || !teamManagementEnabled,
  });
  const [upsertPermission, { isLoading: isUpserting }] = useUpsertPermissionMutation();
  const [deletePermission] = useDeletePermissionMutation();
  const { user } = useAppSelector((state) => state.auth);
  const isOwner = user?.id === ownerId;

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<PermissionLevel>('viewer');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Team add menu
  const [teamMenuAnchor, setTeamMenuAnchor] = useState<null | HTMLElement>(null);

  /* ── invite handler ── */
  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviteError(null);
    try {
      const { supabase } = await import('@/shared/libs/supabase');
      const { data, error } = await supabase.rpc('user_id_by_email', {
        p_email: inviteEmail.trim(),
      });
      if (error || !data) {
        setInviteError('No user found with that email address.');
        return;
      }
      await upsertPermission({
        user_id: data,
        team_id: null,
        object_type: objectType,
        object_id: objectId,
        permission_type: inviteRole,
      }).unwrap();
      setInviteEmail('');
    } catch {
      setInviteError('Failed to invite user. Please try again.');
    }
  };

  const handleAddTeam = async (teamId: string) => {
    setTeamMenuAnchor(null);
    await upsertPermission({
      team_id: teamId,
      object_type: objectType,
      object_id: objectId,
      permission_type: 'viewer',
    }).unwrap();
  };

  const handleChangeLevel = async (
    perm: { user_id?: string | null; team_id?: string | null },
    level: PermissionLevel
  ) => {
    await upsertPermission({
      user_id: perm.user_id ?? null,
      team_id: perm.team_id ?? null,
      object_type: objectType,
      object_id: objectId,
      permission_type: level,
    });
  };

  const handleRemove = async (permId: string) => {
    await deletePermission(permId).unwrap();
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl || window.location.href);
    setLinkCopied(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleInvite();
  };

  /* ── derived data ── */
  const userPerms = useMemo(
    () => perms.filter((p) => p.user_id && p.user_id !== ownerId),
    [perms, ownerId]
  );
  const teamPerms = useMemo(
    () => (teamManagementEnabled ? perms.filter((p) => p.team_id) : []),
    [perms, teamManagementEnabled]
  );
  const sharedTeamIds = useMemo(() => new Set(teamPerms.map((p) => p.team_id)), [teamPerms]);
  const availableTeams = useMemo(
    () => (teamManagementEnabled ? teams.filter((t) => !sharedTeamIds.has(t.id)) : []),
    [teamManagementEnabled, teams, sharedTeamIds]
  );
  const ownerName = useMemo(() => {
    const ownerPerm = perms.find((p) => p.user_id === ownerId);
    return (
      ownerPerm?.user_name ||
      ownerPerm?.user_email ||
      (user?.id === ownerId ? (user?.email ?? 'You') : 'Owner')
    );
  }, [perms, ownerId, user]);
  const ownerEmail = useMemo(
    () =>
      perms.find((p) => p.user_id === ownerId)?.user_email ??
      (user?.id === ownerId ? user?.email : undefined),
    [perms, ownerId, user]
  );
  const ownerAvatarUrl = useMemo(() => {
    const ownerPerm = perms.find((p) => p.user_id === ownerId);
    return (
      ownerPerm?.user_avatar_url ??
      (user?.id === ownerId
        ? (user?.user_metadata?.avatar_url ?? user?.user_metadata?.picture ?? null)
        : null)
    );
  }, [perms, ownerId, user]);
  const invitePlaceholder = teamManagementEnabled
    ? 'Add people, groups, or emails'
    : 'Add people or emails';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: '28px', overflow: 'hidden' } }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          pt: 2.5,
          pb: 0.5,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.15rem' }}>
          Share{title ? ` "${title}"` : ''}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, pt: 1, pb: 2.5, '&:first-of-type': { pt: 1 } }}>
        {/* ── Add people bar ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '28px',
            px: 1.5,
            py: 0.25,
            mb: 0.5,
            '&:focus-within': {
              borderColor: 'primary.main',
              boxShadow: '0 0 0 1px',
              boxShadowColor: 'primary.main',
            },
          }}
        >
          <PersonAddIcon sx={{ color: 'text.secondary', fontSize: 20, mr: 0.5 }} />
          <InputBase
            fullWidth
            placeholder={invitePlaceholder}
            value={inviteEmail}
            onChange={(e) => {
              setInviteEmail(e.target.value);
              setInviteError(null);
            }}
            onKeyDown={handleKeyDown}
            sx={{ flex: 1, fontSize: 14, py: 0.75 }}
          />
          <Select
            variant="standard"
            disableUnderline
            size="small"
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value as PermissionLevel)}
            sx={{
              fontSize: 13,
              color: 'text.secondary',
              minWidth: 80,
              '& .MuiSelect-select': { py: 0 },
            }}
          >
            <MenuItem value="viewer">Viewer</MenuItem>
            <MenuItem value="editor">Editor</MenuItem>
          </Select>
          {inviteEmail.trim() && (
            <Button
              variant="contained"
              size="small"
              disableElevation
              disabled={isUpserting}
              onClick={handleInvite}
              sx={{
                borderRadius: '20px',
                textTransform: 'none',
                minWidth: 0,
                px: 2,
                fontWeight: 500,
              }}
            >
              {isUpserting ? <CircularProgress size={18} color="inherit" /> : 'Send'}
            </Button>
          )}
        </Box>
        {inviteError && (
          <Alert severity="error" sx={{ mt: 0.5, mb: 0.5, borderRadius: 2 }}>
            {inviteError}
          </Alert>
        )}

        {/* ── People with access ── */}
        <Typography variant="body2" sx={{ fontWeight: 500, mt: 2, mb: 0.5, color: 'text.primary' }}>
          People with access
        </Typography>
        <List disablePadding>
          {/* Owner row */}
          <ListItem
            disableGutters
            sx={{ py: 0.75 }}
            secondaryAction={
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 13, pr: 0.5 }}>
                Owner
              </Typography>
            }
          >
            <ListItemAvatar sx={{ minWidth: 44 }}>
              <UserAvatar
                userId={ownerId}
                name={ownerName}
                email={ownerEmail}
                src={ownerAvatarUrl}
              />
            </ListItemAvatar>
            <ListItemText
              primary={
                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                  {ownerName}
                  {user?.id === ownerId ? ' (you)' : ''}
                </Typography>
              }
              secondary={
                ownerEmail ? (
                  <Typography variant="caption" color="text.secondary">
                    {ownerEmail}
                  </Typography>
                ) : undefined
              }
            />
          </ListItem>

          {/* Shared users */}
          {userPerms.map((p) => {
            const name = p.user_name || p.user_email || 'User';
            const email = p.user_name ? p.user_email : undefined;
            return (
              <ListItem
                key={p.id}
                disableGutters
                sx={{ py: 0.75 }}
                secondaryAction={
                  isOwner ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Select
                        variant="standard"
                        disableUnderline
                        size="small"
                        value={p.permission_type}
                        onChange={(e) => handleChangeLevel(p, e.target.value as PermissionLevel)}
                        sx={{
                          fontSize: 13,
                          color: 'text.secondary',
                          '& .MuiSelect-select': { py: 0 },
                        }}
                      >
                        <MenuItem value="viewer">Viewer</MenuItem>
                        <MenuItem value="editor">Editor</MenuItem>
                        <Divider />
                        <MenuItem sx={{ color: 'error.main' }} onClick={() => handleRemove(p.id)}>
                          Remove access
                        </MenuItem>
                      </Select>
                    </Box>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', fontSize: 13, pr: 0.5 }}
                    >
                      {ROLE_LABELS[p.permission_type]}
                    </Typography>
                  )
                }
              >
                <ListItemAvatar sx={{ minWidth: 44 }}>
                  <UserAvatar
                    userId={p.user_id}
                    name={p.user_name}
                    email={p.user_email}
                    src={p.user_avatar_url}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                      {name}
                    </Typography>
                  }
                  secondary={
                    email ? (
                      <Typography variant="caption" color="text.secondary">
                        {email}
                      </Typography>
                    ) : undefined
                  }
                />
              </ListItem>
            );
          })}

          {/* Shared teams */}
          {teamPerms.map((p) => {
            const name = p.team_name || 'Team';
            return (
              <ListItem
                key={p.id}
                disableGutters
                sx={{ py: 0.75 }}
                secondaryAction={
                  isOwner ? (
                    <Select
                      variant="standard"
                      disableUnderline
                      size="small"
                      value={p.permission_type}
                      onChange={(e) => handleChangeLevel(p, e.target.value as PermissionLevel)}
                      sx={{
                        fontSize: 13,
                        color: 'text.secondary',
                        '& .MuiSelect-select': { py: 0 },
                      }}
                    >
                      <MenuItem value="viewer">Viewer</MenuItem>
                      <MenuItem value="editor">Editor</MenuItem>
                      <Divider />
                      <MenuItem sx={{ color: 'error.main' }} onClick={() => handleRemove(p.id)}>
                        Remove access
                      </MenuItem>
                    </Select>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', fontSize: 13, pr: 0.5 }}
                    >
                      {ROLE_LABELS[p.permission_type]}
                    </Typography>
                  )
                }
              >
                <ListItemAvatar sx={{ minWidth: 44 }}>
                  <Avatar sx={{ width: 32, height: 32, fontSize: 14, bgcolor: '#5f6368' }}>
                    <GroupsIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                      {name}
                    </Typography>
                  }
                />
              </ListItem>
            );
          })}
        </List>

        {/* ── Add a team (if teams available) ── */}
        {teamManagementEnabled && isOwner && availableTeams.length > 0 && (
          <>
            <Button
              size="small"
              startIcon={<GroupsIcon />}
              onClick={(e) => setTeamMenuAnchor(e.currentTarget)}
              disabled={isUpserting}
              sx={{
                textTransform: 'none',
                mt: 0.5,
                ml: -0.5,
                fontWeight: 400,
                color: 'primary.main',
              }}
            >
              Add a team
            </Button>
            <Menu
              anchorEl={teamMenuAnchor}
              open={Boolean(teamMenuAnchor)}
              onClose={() => setTeamMenuAnchor(null)}
            >
              {availableTeams.map((t) => (
                <MenuItem key={t.id} onClick={() => handleAddTeam(t.id)}>
                  {t.name}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}

        {/* ── General access (public toggle) ── */}
        {onTogglePublic && (
          <>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, mt: 2, mb: 0.5, color: 'text.primary' }}
            >
              General access
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: isPublic ? 'success.light' : 'action.selected',
                }}
              >
                {isPublic ? (
                  <PublicIcon sx={{ fontSize: 18 }} />
                ) : (
                  <LockIcon sx={{ fontSize: 18 }} />
                )}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                  {isPublic ? 'Anyone with the link' : 'Restricted'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {isPublic ? 'Anyone with the link can view' : 'Only people with access can open'}
                </Typography>
              </Box>
              {isOwner && (
                <Switch
                  checked={!!isPublic}
                  onChange={async (_, checked) => {
                    setToggleError(null);
                    try {
                      await onTogglePublic(checked);
                    } catch {
                      setToggleError('Failed to update visibility. Please try again.');
                    }
                  }}
                  size="small"
                />
              )}
            </Box>
            {toggleError && (
              <Alert
                severity="error"
                sx={{ mt: 1, borderRadius: 2 }}
                onClose={() => setToggleError(null)}
              >
                {toggleError}
              </Alert>
            )}
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {/* ── Bottom bar: Copy link + Done ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            startIcon={<LinkIcon />}
            onClick={handleCopyLink}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              fontWeight: 500,
              borderColor: 'divider',
              color: 'text.primary',
            }}
          >
            Copy link
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={onClose}
            sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 500, px: 3 }}
          >
            Done
          </Button>
        </Box>
      </DialogContent>

      <Snackbar
        open={linkCopied}
        autoHideDuration={2500}
        onClose={() => setLinkCopied(false)}
        message={
          isPublic ? 'Public link copied — any signed-in user can view' : 'Link copied to clipboard'
        }
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  );
};

export default ShareDialog;
